import type { PayRun } from '../../data/types';
import type { OAuthDisbursementBankId } from '../../lib/auth/oauth_pay_run_post_actions';
import type {
  RealStatutoryPaymentRow,
  RealStatutoryPaymentType,
} from '../../lib/auth/oauth_statutory_filing';

/**
 * Pure business logic backing `PostRunStep` (`wizard_steps.tsx`) — money/date
 * validation, row-shaping, and other calculations that don't need React, per
 * the ">150 lines -> extract" component-testability rule.
 */

/**
 * `payroll.generatePayslip`'s locale enum only covers en/si/ta.
 * `useTranslation('wp')`'s `locale` is typed as a plain `string`
 * (`@mukil/i18n`'s `Locale` alias), not narrowed to `WageProLocale`, so this
 * takes a `string` and validates it against the closed set rather than
 * assuming the caller's value is one of the three — same "never assume,
 * always validate an externally-typed string against a closed set, default
 * safely" stance as `wagepro_store_pay_runs.ts`'s `deriveValidRealPayRunStatus`.
 * Defaults to `'en'` for any unrecognised value (WagePro has no other
 * runtime locale today, so this should never actually trigger).
 */
export function map_wagepro_locale_to_payslip_locale(
  locale: string,
): 'en' | 'si' | 'ta' {
  switch (locale) {
    case 'si-LK':
      return 'si';
    case 'ta-LK':
      return 'ta';
    default:
      return 'en';
  }
}

/** A pay run's salary disbursement file may only be generated once it is approved — mirrors the server's own `getSalaryDisbursementFile` gate exactly (see `apps/api/src/trpc/routers/payroll.ts`), so the UI disables the action instead of letting the user hit a guaranteed `BAD_REQUEST`. */
export function can_generate_disbursement_file(run: PayRun): boolean {
  return run.status === 'approved';
}

/** Statutory remittances may only be recorded against an approved pay run — mirrors `recordStatutoryPayment`'s own server-side gate. */
export function can_record_statutory_payment(run: PayRun): boolean {
  return run.status === 'approved';
}

/**
 * Matches the server's own `ISO_DATE` zod schema
 * (`apps/api/src/trpc/routers/payroll.ts`) exactly — shared by every dialog
 * in this module that submits a date field, so an empty or malformed date
 * is caught client-side with a specific error instead of reaching the
 * server as a raw zod-regex `BAD_REQUEST`.
 */
export function is_valid_iso_date(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

export type DisbursementValidationError =
  | 'bank_required'
  | 'account_required'
  | 'date_required';

/**
 * Validates the minimal salary-disbursement dialog's inputs before calling
 * the server — never sends an incomplete request just to have the server
 * reject it with a less specific error. Returns every violated rule (not
 * just the first) so the dialog can show all of them at once.
 */
export function validate_disbursement_input(input: {
  bank_id: OAuthDisbursementBankId | null;
  debit_account_no: string;
  value_date: string;
}): DisbursementValidationError[] {
  const errors: DisbursementValidationError[] = [];
  if (input.bank_id === null) errors.push('bank_required');
  if (input.debit_account_no.trim() === '') errors.push('account_required');
  if (!is_valid_iso_date(input.value_date)) {
    errors.push('date_required');
  }
  return errors;
}

/** Every statutory remittance type, in the fixed display order the section renders. */
export const STATUTORY_PAYMENT_TYPES: readonly RealStatutoryPaymentType[] = [
  'epf_employee',
  'epf_employer',
  'etf',
  'paye',
];

export interface StatutoryPaymentRow {
  payment_type: RealStatutoryPaymentType;
  computed_due_cents: number;
  recorded: RealStatutoryPaymentRow | null;
}

/**
 * Joins the server's computed-due amounts with any already-recorded payment
 * per type, in {@link STATUTORY_PAYMENT_TYPES} order — mirrors
 * `apps/ledger-web/src/routes/entities/payroll/statutory_payments_calculations.ts`'s
 * `build_statutory_payment_rows` (a sibling app, not imported cross-app; see
 * `docs/architecture-boundaries.md` — every product app owns its own UI
 * logic even when the shape happens to match).
 *
 * `listStatutoryPayments` only ever returns ACTIVE (non-voided) payments, so
 * at most one recorded row exists per type — if the wire data ever violated
 * that (a server regression), the FIRST match wins rather than throwing.
 */
export function build_statutory_payment_rows(
  computed_due_cents: Record<RealStatutoryPaymentType, number>,
  payments: readonly RealStatutoryPaymentRow[],
): StatutoryPaymentRow[] {
  return STATUTORY_PAYMENT_TYPES.map((payment_type) => ({
    payment_type,
    computed_due_cents: computed_due_cents[payment_type],
    recorded: payments.find((p) => p.payment_type === payment_type) ?? null,
  }));
}

/**
 * Splits a canonical filing-obligation `report_id` (`${obligation}:${periodLabel}`,
 * e.g. `'vat:2026-07'`) into its two parts for `filingCalendar.markFiled`'s
 * input — mirrors the exact format `apps/api/src/trpc/routers/filing_calendar.ts`
 * writes. Returns `null` for a legacy, non-canonical report_id (no colon —
 * see that router's own `calendar` procedure, which treats a colon-free
 * report_id the same way), so the caller can hide the "mark filed" action
 * for a row it cannot safely translate back into `markFiled`'s input shape
 * rather than guessing.
 */
export function parse_filing_report_id(
  report_id: string,
): { obligation: string; period_label: string } | null {
  const separator_index = report_id.indexOf(':');
  if (separator_index === -1) return null;
  const obligation = report_id.slice(0, separator_index);
  const period_label = report_id.slice(separator_index + 1);
  if (obligation === '' || period_label === '') return null;
  return { obligation, period_label };
}

/**
 * Decodes a base64-encoded binary payload (e.g. `generatePayslip`'s
 * `pdf_base64`) into a downloadable `Blob`. Pure and DOM-light (only touches
 * the ambient `atob`, available in both browsers and bun's test runtime) so
 * it's unit-testable without mocking any DOM API.
 */
export function decode_base64_to_blob(base64: string, mime_type: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime_type });
}
